import React, { lazy, Suspense, useEffect } from 'react';

import { ReviewsProvider } from '@/context/reviews';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { LanguageProvider, useI18n } from '@/i18n/LanguageProvider';
import { Canonicalize } from '@/utils/Canonicalize';
import LegacyHierRedirect from '@/utils/LegacyHierRedirect';
import LegacyFlatRedirect from '@/utils/LegacyFlatRedirect';
import { ParamsProvider, useLocation } from '@/utils/router';
import type { EventType } from '@/constants/geo';
import {
  resolveLocalizedGlobalTypeRoute,
  type LocalizedSeoLang,
} from '@/constants/localized-routes';

const HomePage = lazy(() => import('@/pages/HomePage'));
const TargetPage = lazy(() => import('@/pages/TargetPage'));
const AccessibilityPage = lazy(() => import('@/pages/AccessibilityPage'));
const PlanningGuidePage = lazy(() => import('@/pages/PlanningGuidePage'));
const PlanningArticlePage = lazy(() => import('@/pages/PlanningArticlePage'));

const trimRoutePath = (pathname: string) => {
  const path = pathname || '/';
  if (path === '/') return path;
  return path.replace(/\/+$/g, '');
};

function LocalizedGlobalTargetRoute({
  lang,
  type,
}: {
  lang: LocalizedSeoLang;
  type: EventType;
}) {
  const { lang: currentLang, setLang } = useI18n();

  useEffect(() => {
    if (currentLang !== lang) {
      setLang(lang);
    }
  }, [currentLang, lang, setLang]);

  if (currentLang !== lang) return null;

  return (
    <ParamsProvider params={{ type }}>
      <TargetPage />
    </ParamsProvider>
  );
}

function RouteElement() {
  const location = useLocation();
  const path = trimRoutePath(location.pathname);
  const segments = path.split('/').filter(Boolean);
  const localizedGlobalTarget = resolveLocalizedGlobalTypeRoute(path);

  if (path === '/') return <HomePage />;

  if (localizedGlobalTarget) {
    return (
      <LocalizedGlobalTargetRoute
        lang={localizedGlobalTarget.lang}
        type={localizedGlobalTarget.type}
      />
    );
  }

  if (segments[0] === 'הצהרת-נגישות') {
    return <AccessibilityPage />;
  }

  if (segments[0] === 'מדריך-אירועים') {
    if (segments[1]) {
      return (
        <ParamsProvider params={{ slug: segments[1] }}>
          <PlanningArticlePage />
        </ParamsProvider>
      );
    }

    return <PlanningGuidePage />;
  }

  if (segments[0] === 'תקליטן' && segments[1]) {
    return (
      <ParamsProvider params={{ type: segments[1], location: segments[2] }}>
        <TargetPage />
      </ParamsProvider>
    );
  }

  if (segments[0]?.startsWith('תקליטן-')) {
    return (
      <ParamsProvider
        params={{ flatSlug: segments[0].replace(/^תקליטן-/, '') }}
      >
        <LegacyFlatRedirect />
      </ParamsProvider>
    );
  }

  if (segments[2] === 'תקליטן') {
    return (
      <ParamsProvider
        params={{
          region: segments[0],
          city: segments[1],
          type: segments[3],
        }}
      >
        <LegacyHierRedirect />
      </ParamsProvider>
    );
  }

  if (segments[1] === 'תקליטן') {
    return (
      <ParamsProvider params={{ region: segments[0] }}>
        <LegacyHierRedirect />
      </ParamsProvider>
    );
  }

  return <HomePage />;
}

export default function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <ReviewsProvider>
          <Canonicalize />
          <Suspense fallback={null}>
            <RouteElement />
          </Suspense>
        </ReviewsProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}
